import { useLayoutEffect, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

import LogoutSuccess from "../logout/LogoutSuccess";
import AccessDenied from "../access-denied/AccessDenied";
import AvatarChatBot from "../avatorChatbox/avatorChatbox";
import Authentication from "../Authentication/Authentication";
import ProtectedRoute from "../../routes/ProtectedRoute";

import { FilterProvider } from "../../context/FilterContext";
import { removeFromLocalStorage } from "../../utils/localStorage";

import Dashboard from "../../pages/Dashboard/Dashboard";
import GrowthHackTableData from "../../pages/GrowthHackTableData/GrowthHackTableData";

/**
 * MainComponent
 *
 * This component handles the application routing.
 * Authentication logic has been moved to the Authentication component.
 * Protected routes are wrapped with ProtectedRoute component.
 */
export default function MainComponent(props) {
  const location = useLocation();
  const { instance } = useMsal();

  /**
   * useEffect:
   * Check for user inactivity and log out after 30 minutes
   */
  useEffect(() => {
    const events = ['mousemove', 'keydown', 'scroll', 'click'];
    let timeout;

    const resetTimer = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        console.warn("User inactive. Logging out...");
        sessionStorage.clear();
        localStorage.clear();
        // Redirect to the GTM Factory Dashboard using env variable
        window.location.href = process.env.REACT_APP_DASHBOARD_URL;
      }, 30 * 60 * 1000); // 30 minutes
    };

    events.forEach(event => window.addEventListener(event, resetTimer));
    resetTimer(); // Start timer on mount

    return () => {
      events.forEach(event => window.removeEventListener(event, resetTimer));
      clearTimeout(timeout);
    };
  }, [instance]);

  /**
   * Clear growth hacks page state when navigating away
   */
  useEffect(() => {
    if (location.pathname === "/") {
      removeFromLocalStorage("growthHacksCurrentPage");
    }
  }, [location.pathname]);

  /**
   * Set document title on initial render
   */
  useLayoutEffect(() => {
    document.title = "Mobility - GTM Factory";
  }, []);

  return (
    <Routes>
      {/* Authentication Route - Public */}
      <Route path="/auth" element={<Authentication />} />
      
      {/* Protected Routes - Require Authentication */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <AvatarChatBot {...props} />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <FilterProvider>
              <Dashboard />
            </FilterProvider>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/growthhack-tabledata" 
        element={
          <ProtectedRoute>
            <GrowthHackTableData />
          </ProtectedRoute>
        } 
      />
      
      {/* Public Routes */}
      <Route path="/logout-success" element={<LogoutSuccess />} />
      <Route path="/access-denied" element={<AccessDenied />} />
      <Route path="*" status={404} element={<>Page not found</>} />
    </Routes>
  );
}
